import api from '@services/api';
import { handleErrors } from '@services/helpers';
import { rootApi } from '@store/root-api';
import appHelpers from '@utils/app-urls';
import {
  CreatedCryptoDepositDetailsDto,
  CreateFiatDepositDto,
  CryptoDepositProductDto,
  DepositCryptoApi,
  DepositFiatApi,
  FiatDepositProductDataDto,
  PendingFiatDepositDetailsDto,
} from 'services/generated';

const API_URL = appHelpers.getEndpointUrl();

const depositFiatClient = new DepositFiatApi(
  undefined,
  new URL(API_URL, process.env.REACT_API_URL).href,
  api,
);

const depositCryptoClient = new DepositCryptoApi(
  undefined,
  new URL(API_URL, process.env.REACT_API_URL).href,
  api,
);

type FiatPaymentMadeRequest = {
  orderId: string;
  paymentMade?: boolean;
  popFile?: File;
};

const depositApi = rootApi.injectEndpoints({
  endpoints: builder => ({
    getFiatDepositProducts: builder.query<FiatDepositProductDataDto, void>({
      queryFn: () =>
        handleErrors(() => depositFiatClient.getFiatDepositProductData()),
      providesTags: [{ type: 'FiatDeposit' }],
    }),
    getPendingFiatDeposits: builder.query<PendingFiatDepositDetailsDto[], void>(
      {
        queryFn: () =>
          handleErrors(() =>
            depositFiatClient.getPendingFiatDepositOrderDetails(),
          ),
        providesTags: [{ type: 'PendingFiatDeposit' }],
      },
    ),
    hideDepositInstructions: builder.mutation<any, void>({
      queryFn: () =>
        handleErrors(() => depositFiatClient.hideDepositInstructions()),
      invalidatesTags: [{ type: 'FiatDeposit' }],
    }),
    createFiatDeposit: builder.mutation<any, CreateFiatDepositDto>({
      queryFn: request =>
        handleErrors(() => depositFiatClient.createFiatDeposit(request)),
      invalidatesTags: [
        { type: 'PendingFiatDeposit' },
        { type: 'PendingTransactions' },
        { type: 'UserProfile' },
      ],
    }),
    cancelFiatDeposit: builder.mutation<void, string>({
      queryFn: orderId => handleErrors(() => depositFiatClient.cancel(orderId)),
      invalidatesTags: [
        { type: 'PendingFiatDeposit' },
        { type: 'PendingTransactions' },
        { type: 'UserProfile' },
      ],
    }),
    fiatPaymentMade: builder.mutation<void, FiatPaymentMadeRequest>({
      queryFn: request =>
        handleErrors(() =>
          depositFiatClient.paymentMade(
            request.orderId,
            request.paymentMade,
            request.popFile,
          ),
        ),
      invalidatesTags: [
        { type: 'PendingFiatDeposit' },
        { type: 'PendingTransactions' },
      ],
    }),
    getCryptoDepositProducts: builder.query<CryptoDepositProductDto[], void>({
      queryFn: () =>
        handleErrors(() => depositCryptoClient.availableProducts()),
      providesTags: [{ type: 'CryptoDeposit' }],
    }),
    createCryptoDepositAddress: builder.mutation<
      CreatedCryptoDepositDetailsDto,
      string
    >({
      queryFn: productCode =>
        handleErrors(() => depositCryptoClient.createDeposit(productCode)),
      invalidatesTags: (result, error, productCode) => [
        { type: 'PendingCryptoDeposit', id: productCode },
        { type: 'CryptoDeposit' },
        { type: 'PendingTransactions' },
        { type: 'UserProfile' },
      ],
    }),
    getCryptoDepositAddress: builder.query<
      CreatedCryptoDepositDetailsDto,
      string
    >({
      queryFn: productCode =>
        handleErrors(() => depositCryptoClient.getDeposit(productCode)),
      providesTags: (result, error, productCode) => [
        { type: 'PendingCryptoDeposit', id: productCode },
      ],
    }),
    cancelCryptoDeposit: builder.mutation<void, string>({
      queryFn: orderId =>
        handleErrors(() => depositCryptoClient.cancel(orderId)),
      invalidatesTags: (result, error, orderId) => [
        { type: 'CryptoDeposit' },
        { type: 'UserProfile' },
      ],
    }),
    cryptoDepositMade: builder.mutation<void, string>({
      queryFn: orderId =>
        handleErrors(() => depositCryptoClient.depositMade(orderId)),
    }),
  }),
});

export const {
  useGetFiatDepositProductsQuery,
  useGetPendingFiatDepositsQuery,
  useHideDepositInstructionsMutation,
  useCreateFiatDepositMutation,
  useCancelFiatDepositMutation,
  useGetCryptoDepositProductsQuery,
  useGetCryptoDepositAddressQuery,
  useCreateCryptoDepositAddressMutation,
  useCancelCryptoDepositMutation,
  useFiatPaymentMadeMutation,
  useCryptoDepositMadeMutation,
} = depositApi;
